import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';
import { IconType } from '@ng-icons/core';
import { Address } from 'src/app/modules/route-planner/interfaces/location/address.interface';

@Component({
    selector: 'app-address-auto-complete-result',
    templateUrl: './address-auto-complete-result.component.html',
    styleUrls: ['./address-auto-complete-result.component.scss'],
    standalone: false
})
export class AddressAutoCompleteResultComponent {
  static DEFAULT_ICON: IconType = 'location-dot';

  @Output() onResultClick: EventEmitter<Address> = new EventEmitter<Address>();
  @Output() optionFocus = new EventEmitter<void>();

  @Input() address: Address;
  @Input() disabled: boolean;
  @Input() main: string;
  @Input() sub: string;
  @Input() icon: IconType = AddressAutoCompleteResultComponent.DEFAULT_ICON;
  @Input() isSubBlue: boolean = false;

  /**
   * a11y: when used inside a combobox-autocomplete-list listbox, render the row as
   * `<li role="option">` instead of `<button>`. DOM focus stays on the combobox
   * input; this option is selected via aria-activedescendant and click/Enter.
   * Outside that context (feed: "current location", favorites, recent searches),
   * keep the `<button>` so it remains a real focusable shortcut.
   */
  @Input() optionMode: boolean = false;
  /** Stable id so the combobox input can reference this option via aria-activedescendant. */
  @Input() optionId: string;
  /** Drives `aria-selected` + visual highlight when this option is the active descendant. */
  @Input() isActive: boolean = false;

  /**
   * a11y: when this button opens a secondary popup (a dialog/menu rather than just
   * picking the address and closing the parent), set `aria-haspopup`. Allowed values
   * follow ARIA spec — most popup-launchers in this app should use `"dialog"`.
   * `null` (default) means the button does not open a popup.
   *
   * Used by:
   *   - "Search on map" rows (open map overlay dialog).
   *   - "Home" / "Work" rows when the address isn't set yet (open setup dialog).
   *   - In all other cases (just picking an existing address) leave this unset.
   */
  @Input() ariaHasPopup: 'dialog' | 'menu' | 'listbox' | 'tree' | 'grid' | null = null;

  /**
   * a11y: when this component is rendered inside a <ul role="listbox">, the Angular
   * host element (`<app-address-auto-complete-result>`) sits between the listbox
   * and the inner <li role="option">, which technically breaks the required
   * parent-child relationship. `display: contents` removes the host box from both
   * the visual layout AND the accessibility tree so the listbox->option link holds.
   * Role="presentation" is added as a belt-and-suspenders hint to AT.
   */
  @HostBinding('style.display') get hostDisplay(): string {
    return this.optionMode ? 'contents' : '';
  }
  @HostBinding('attr.role') get hostRole(): string | null {
    return this.optionMode ? 'presentation' : null;
  }

  handleClick(): void {
    if (!this.disabled) {
      this.onResultClick.emit(this.address);
    }
  }

  emitOptionFocus(): void {
    this.optionFocus.emit();
  }

  /** Keep DOM focus on the combobox input when the user mouses on an option. */
  preventFocusSteal(event: MouseEvent): void {
    if (this.optionMode) {
      event.preventDefault();
    }
  }
}
